/**
 * Pragmatic Firestore presence for Messages.
 *
 * Model: presence/{uid} { state: 'online'|'offline', lastSeen: Timestamp }
 * Online = state === 'online' AND lastSeen within ONLINE_TTL_MS (stale heartbeats drop).
 * Heartbeat ~45s while tab visible; best-effort offline on hide/unload.
 * Respects privacySettings.showOnlineStatus (no public online write when off).
 */
import { db } from './config';
import {
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

/** Consider online if lastSeen is fresher than this */
export const ONLINE_TTL_MS = 2 * 60 * 1000;
/** Heartbeat interval while session is active */
export const PRESENCE_HEARTBEAT_MS = 45 * 1000;

export function isPresenceOnline(presenceDoc) {
  if (!presenceDoc || presenceDoc.state !== 'online' || !presenceDoc.lastSeen) {
    return false;
  }
  try {
    const last =
      typeof presenceDoc.lastSeen.toMillis === 'function'
        ? presenceDoc.lastSeen.toMillis()
        : presenceDoc.lastSeen instanceof Date
          ? presenceDoc.lastSeen.getTime()
          : new Date(presenceDoc.lastSeen).getTime();
    if (!Number.isFinite(last)) return false;
    return Date.now() - last < ONLINE_TTL_MS;
  } catch {
    return false;
  }
}

async function writePresence(userId, state) {
  if (!userId) return;
  const ref = doc(db, 'presence', userId);
  await setDoc(
    ref,
    {
      state,
      lastSeen: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Mark online (or no-op / force offline when privacy hides status).
 */
export async function setUserOnline(userId, { showOnlineStatus = true } = {}) {
  try {
    if (!showOnlineStatus) {
      await writePresence(userId, 'offline');
      return;
    }
    await writePresence(userId, 'online');
  } catch (error) {
    console.warn('setUserOnline failed:', error?.message || error);
  }
}

export async function setUserOffline(userId) {
  try {
    await writePresence(userId, 'offline');
  } catch (error) {
    console.warn('setUserOffline failed:', error?.message || error);
  }
}

/**
 * Subscribe to one user's presence. Callback receives { state, lastSeen, isOnline }.
 */
export function listenToUserPresence(userId, callback) {
  if (!userId) {
    callback({ state: 'offline', lastSeen: null, isOnline: false });
    return () => {};
  }
  const ref = doc(db, 'presence', userId);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback({ state: 'offline', lastSeen: null, isOnline: false });
        return;
      }
      const data = snap.data();
      callback({
        state: data.state || 'offline',
        lastSeen: data.lastSeen || null,
        isOnline: isPresenceOnline(data),
      });
    },
    (error) => {
      console.warn('Presence listener error:', error);
      callback({ state: 'offline', lastSeen: null, isOnline: false });
    }
  );
}

/**
 * Subscribe to many users' presence.
 * Callback receives { [uid]: { isOnline, lastSeen, state } }.
 */
export function listenToUsersPresence(userIds, callback) {
  const ids = [...new Set((userIds || []).filter(Boolean))];
  if (ids.length === 0) {
    callback({});
    return () => {};
  }

  const raw = {};
  const emit = () => {
    const map = {};
    for (const uid of ids) {
      const entry = raw[uid] || { state: 'offline', lastSeen: null };
      map[uid] = {
        isOnline: isPresenceOnline(entry),
        lastSeen: entry.lastSeen || null,
        state: entry.state || 'offline',
      };
    }
    callback(map);
  };

  const unsubs = ids.map((uid) =>
    listenToUserPresence(uid, (presence) => {
      raw[uid] = {
        state: presence.state,
        lastSeen: presence.lastSeen,
      };
      emit();
    })
  );

  // Re-evaluate TTL periodically so stale "online" clears without a write
  const tick = setInterval(emit, 30 * 1000);

  return () => {
    clearInterval(tick);
    unsubs.forEach((u) => u && u());
  };
}

/** Client clock helper for offline lastSeen display */
export function presenceLastSeenDate(lastSeen) {
  if (!lastSeen) return null;
  if (typeof lastSeen.toDate === 'function') return lastSeen.toDate();
  if (lastSeen instanceof Timestamp) return lastSeen.toDate();
  const d = new Date(lastSeen);
  return Number.isNaN(d.getTime()) ? null : d;
}
